import {
  TaskTranscriptService,
  type TieInListItem,
  type TieInListPublic,
  type TieInReference,
  type TieInReferencesPublic,
  type TieInReferenceType,
} from "@common/client"
import {
  type InfiniteData,
  useQuery,
  useSuspenseInfiniteQuery,
} from "@tanstack/react-query"
import { useMemo } from "react"
import { makeAxiosMutation, makeQuery, unwrap } from "./utils"

export const allTaskTranscriptsBaseKey = ["task-transcripts"] as const

export const latestTieInsReferencesKey = ["latest-tie-ins-references"] as const

export function taskTranscriptsByWorkPackageKey(workPackageId: string) {
  return [...allTaskTranscriptsBaseKey, "work-package", workPackageId]
}

export const taskTranscriptsByWorkPackageQuery = makeQuery({
  serviceCall: TaskTranscriptService.getTaskTranscriptsByWorkPackageId,
  queryKey: ({ work_package_id }) =>
    taskTranscriptsByWorkPackageKey(work_package_id),
})

export const tieInsKey = [...allTaskTranscriptsBaseKey, "tie-ins"] as const

export function tieInKey(id: string) {
  return [...tieInsKey, id] as const
}

export function useTieIn(id: string) {
  return useQuery({
    queryKey: tieInKey(id),
    queryFn: () => TaskTranscriptService.getTieIn({ id }).then(unwrap),
  })
}

export function taskTranscriptsByWorkTaskKey(workTaskId: string) {
  return [...allTaskTranscriptsBaseKey, workTaskId]
}

export const taskTranscriptsByWorkTaskQuery = makeQuery({
  serviceCall: TaskTranscriptService.getTaskTranscriptsByWorkTaskId,
  queryKey: ({ work_task_id }) => taskTranscriptsByWorkTaskKey(work_task_id),
})

export const useAssignUnassignedTieInMutation = makeAxiosMutation({
  serviceCall: TaskTranscriptService.updateItem,
  invalidates: () => [tieInsInfiniteKey, latestTieInsReferencesKey],
})

// --- Infinite list hooks ---

const TIE_INS_PER_PAGE = 20

export const tieInsInfiniteKey = [...tieInsKey, "infinite"] as const

export function useTieInsInfinite(search: string, unassigned = false) {
  const {
    data,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSuspenseInfiniteQuery<
    TieInListPublic,
    Error,
    InfiniteData<TieInListPublic>,
    readonly unknown[],
    number
  >({
    queryKey: [...tieInsInfiniteKey, search || null, unassigned],
    queryFn: ({ pageParam }) =>
      TaskTranscriptService.getTieIns({
        skip: pageParam,
        limit: TIE_INS_PER_PAGE,
        search: search || undefined,
        unassigned: unassigned || undefined,
      }).then(unwrap),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * TIE_INS_PER_PAGE
      return loaded < (lastPage.count ?? 0) ? loaded : undefined
    },
    initialPageParam: 0,
    staleTime: 30_000,
  })

  const tieIns = useMemo(() => {
    const seen = new Set<string>()
    const items: TieInListItem[] = []
    for (const page of data?.pages ?? []) {
      for (const tieIn of page.data) {
        if (seen.has(tieIn.id)) continue
        seen.add(tieIn.id)
        items.push(tieIn)
      }
    }
    return items
  }, [data])
  const totalCount = data?.pages[0]?.count ?? 0

  return {
    tieIns,
    totalCount,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export function useLatestTieInsReferencesInfinite(
  search: string,
  types: TieInReferenceType[] | null,
  loadTieIns = false,
) {
  const {
    data,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSuspenseInfiniteQuery<
    TieInReferencesPublic,
    Error,
    InfiniteData<TieInReferencesPublic>,
    readonly unknown[],
    number
  >({
    queryKey: [
      ...latestTieInsReferencesKey,
      "infinite",
      search || null,
      types,
      loadTieIns,
    ],
    queryFn: ({ pageParam }) =>
      TaskTranscriptService.getLatestTieInsReferences({
        skip: pageParam,
        limit: TIE_INS_PER_PAGE,
        search: search || undefined,
        types,
        load_tie_ins: loadTieIns || undefined,
      }).then(unwrap),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * TIE_INS_PER_PAGE
      return loaded < (lastPage.count ?? 0) ? loaded : undefined
    },
    initialPageParam: 0,
    staleTime: 30_000,
  })

  const references = useMemo(() => {
    const seen = new Set<string>()
    const items: TieInReference[] = []
    for (const page of data?.pages ?? []) {
      for (const reference of page.data) {
        const key = `${reference.reference_type}-${reference.id}`
        if (seen.has(key)) continue
        seen.add(key)
        items.push(reference)
      }
    }
    return items
  }, [data])
  const totalCount = data?.pages[0]?.count ?? 0

  return {
    references,
    totalCount,
    isFetchNextPageError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}
